import { useState } from 'react';
import type { ReactNode } from 'react';
import { AUTONOMY_LEVELS, FORMALITIES, VOICE_MODES } from '@yourstore/concierge-client';
import type { AgentSettingsPatch } from '@yourstore/concierge-client';
import { useRuntime, useLive } from './context';
import { useRemote } from './useRemote';
import { useT } from '../messages';
import type { T } from '../messages';
import type { UiLocale } from '../messages/locale';
import { formatClock } from '../mirror/time';
import { useLocale } from '../messages';
import { ConciergeState } from '../wire';
import type { ConciergePartner, Service } from '../wire';

/**
 * **The AI front desk**, as the owner sees it — the same four verbs the
 * operator console gets, over `core-api` instead of `admin-api`.
 *
 * Three things about this panel are decisions rather than layout.
 *
 * **1. It is online, like the pass and the dock.** `useRemote.ts` argues the
 * general case; here the particular reason is that the answer is a *third
 * party's* state — what the agent has read, what it is still missing — and a
 * cached copy of somebody else's state is not staleness, it is a wrong answer
 * to "is this working". There is nothing to queue either: an autonomy level
 * replayed out of an outbox against a world that moved is exactly the conflict
 * §8.3 refuses to merge.
 *
 * **2. It offers no editor for anything this app already owns.** Hours, prices,
 * policies and the FAQ are edited on the screens that own them and reach the
 * agent through `GET /v1/profile` — so there is deliberately no "hours for the
 * concierge" here, and no way to add one: the patch type is
 * `AgentSettingsPatch`, whose `.strict()` makes it a compile error.
 *
 * **3. The per-service switches join by id, and the ids are ours.** That is the
 * whole point of the synced catalog, and it is why this side can show the
 * owner's own service *names* where the operator console can only show ids —
 * `admin_api` holds no grant on `items` and this app has the catalog mirrored.
 * An id the agent knows and the mirror does not is shown as "not synced yet".
 */
export function ConciergePanel(): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const locale = useLocale();
  const session = useLive(() => runtime.mirror.session(), null);
  const services = useLive(() => runtime.mirror.services(), []);
  const timezone = session?.tenant.timezone ?? 'UTC';

  const state = useRemote(
    (signal) =>
      runtime.api.request({
        method: 'GET',
        path: '/concierge',
        schema: ConciergeState,
        signal,
      }),
    [],
    // Slow: nothing on this screen is a race with a runner or a boat. It is a
    // settings page, and a poll every fifteen seconds would be a request every
    // fifteen seconds to a third party for a value that changes twice a year.
    { pollMs: 60_000 },
  );

  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const call = async (work: () => Promise<unknown>, note: string | null): Promise<void> => {
    setBusy(true);
    setFailure(null);
    setSaved(null);
    try {
      await work();
      setSaved(note);
      state.reload();
    } catch (e) {
      // Verbatim. The server — or the agent behind it — wrote a sentence about
      // this exact request, and paraphrasing it would lose the one thing it
      // knows and this screen does not.
      setFailure(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (state.data === null) return null;
  const data = state.data;

  if (!data.configured) {
    return (
      <article className="panel">
        <h2>{t('concierge.title')}</h2>
        <p className="muted">{t('concierge.notConfigured')}</p>
      </article>
    );
  }

  const partner = data.concierge;
  const paused = partner?.status === 'paused';

  return (
    <article className="panel">
      <h2>{t('concierge.title')}</h2>
      <p className="muted">{t('concierge.note')}</p>

      {data.unreachable === null ? null : (
        <p className="warn">{t('concierge.unreachable', { message: data.unreachable })}</p>
      )}
      {partner === null ? (
        <p className="muted">{t('concierge.notEnabled')}</p>
      ) : (
        <>
          <StatusLine partner={partner} timezone={timezone} locale={locale} />
          <MissingList partner={partner} consoleUrl={data.console_url} />

          <div className="row-actions">
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() =>
                void call(
                  () =>
                    runtime.api.request({
                      method: 'POST',
                      path: '/concierge/sync',
                      body: {},
                      schema: ConciergeState,
                    }),
                  null,
                )
              }
            >
              {t('concierge.sync')}
            </button>
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() =>
                void call(
                  () =>
                    runtime.api.request({
                      method: 'POST',
                      path: '/concierge/status',
                      body: { status: paused ? 'enabled' : 'paused' },
                      schema: ConciergeState,
                    }),
                  null,
                )
              }
            >
              {paused ? t('concierge.resume') : t('concierge.pause')}
            </button>
          </div>
          {paused ? <p className="warn">{t('concierge.paused')}</p> : null}
          <p className="muted">{t('concierge.syncNote')}</p>

          <AgentSettings
            partner={partner}
            busy={busy}
            onSave={(patch) =>
              void call(
                () =>
                  runtime.api.request({
                    method: 'PATCH',
                    path: '/concierge/settings',
                    body: patch,
                    schema: ConciergeState,
                  }),
                t('concierge.saved'),
              )
            }
            onNothing={() => setSaved(t('concierge.nothingChanged'))}
          />

          <ServiceSwitches
            partner={partner}
            services={services}
            busy={busy}
            onToggle={(id, next) =>
              void call(
                () =>
                  runtime.api.request({
                    method: 'PATCH',
                    path: '/concierge/settings',
                    body: { services: [{ id, bookable_by_ai: next }] },
                    schema: ConciergeState,
                  }),
                null,
              )
            }
          />
        </>
      )}

      {failure === null ? null : <p className="warn">{failure}</p>}
      {saved === null ? null : <p className="ok">{saved}</p>}
    </article>
  );
}

function StatusLine({
  partner,
  timezone,
  locale,
}: {
  partner: ConciergePartner;
  timezone: string;
  locale: UiLocale;
}): ReactNode {
  const t = useT();
  const when = (iso: string | null | undefined): string =>
    iso == null ? t('concierge.neverSynced') : formatClock(iso, timezone, locale);

  return (
    <dl className="facts">
      <dt>{t('concierge.status')}</dt>
      {/* Their word, printed as it arrived — see the catalog's header. */}
      <dd>{partner.status ?? '—'}</dd>
      <dt>{t('concierge.knows')}</dt>
      <dd>
        {partner.catalog_source?.version == null
          ? t('concierge.neverSynced')
          : t('concierge.menuVersion', {
              version: String(partner.catalog_source.version),
              when: when(partner.catalog_source.synced_at),
            })}
        {' · '}
        {partner.profile_source?.version == null
          ? t('concierge.neverSynced')
          : t('concierge.detailsVersion', {
              version: String(partner.profile_source.version),
              when: when(partner.profile_source.synced_at),
            })}
      </dd>
    </dl>
  );
}

/**
 * `missing` as a to-do list. A telephone number is the one item nothing in this
 * app can fix — it is assigned in aiconcierge — so it links out, which is what
 * the handoff asks for by name.
 */
function MissingList({
  partner,
  consoleUrl,
}: {
  partner: ConciergePartner;
  consoleUrl: string | null;
}): ReactNode {
  const t = useT();
  const missing = partner.missing ?? [];
  if (missing.length === 0) return null;

  return (
    <>
      <h3>{t('concierge.missing')}</h3>
      <ul className="plain-list">
        {missing.map((key) => (
          <li key={key}>
            {missingSentence(t, key) === '' ? key : missingSentence(t, key)}
            {key === 'phone_number' && consoleUrl !== null ? (
              <>
                {' '}
                <a href={consoleUrl} target="_blank" rel="noreferrer">
                  {t('concierge.openConsole')}
                </a>
              </>
            ) : null}
          </li>
        ))}
      </ul>
    </>
  );
}

/** A `switch`, so a word we have not met renders as itself rather than as blank. */
function missingSentence(t: T, key: string): string {
  switch (key) {
    case 'provider_connection':
      return t('concierge.missing.provider_connection');
    case 'catalog':
      return t('concierge.missing.catalog');
    case 'phone_number':
      return t('concierge.missing.phone_number');
    case 'owner_number':
      return t('concierge.missing.owner_number');
    default:
      return '';
  }
}

/**
 * **Only changed fields are sent**, which is the same rule the service editor
 * on this screen already follows and the reason their `PATCH` is partial: a
 * form that re-asserted everything would turn "I fixed a typo in the greeting"
 * into a write that also re-states an autonomy level somebody set from the
 * other console a minute ago.
 */
function AgentSettings({
  partner,
  busy,
  onSave,
  onNothing,
}: {
  partner: ConciergePartner;
  busy: boolean;
  onSave: (patch: AgentSettingsPatch) => void;
  onNothing: () => void;
}): ReactNode {
  const t = useT();
  const stored = partner.settings ?? {};

  const [autonomy, setAutonomy] = useState(stored.autonomy ?? '');
  const [ceiling, setCeiling] = useState(
    stored.autonomous_value_ceiling_cents == null
      ? ''
      : String(stored.autonomous_value_ceiling_cents),
  );
  const [greeting, setGreeting] = useState(stored.greeting ?? '');
  const [voiceGreeting, setVoiceGreeting] = useState(stored.voice_greeting ?? '');
  const [toneVoice, setToneVoice] = useState(stored.tone?.voice ?? '');
  const [formality, setFormality] = useState(stored.tone?.formality ?? '');
  const [avoid, setAvoid] = useState((stored.tone?.avoid ?? []).join('\n'));
  const [ownerNumber, setOwnerNumber] = useState(stored.owner_number ?? '');
  const [voiceMode, setVoiceMode] = useState(stored.voice_mode ?? '');

  const submit = (): void => {
    const out: Record<string, unknown> = {};
    if (autonomy !== '' && autonomy !== stored.autonomy) out['autonomy'] = autonomy;
    const ceilingNow = ceiling.trim() === '' ? null : Number(ceiling);
    if (ceilingNow !== (stored.autonomous_value_ceiling_cents ?? null)) {
      out['autonomous_value_ceiling_cents'] = ceilingNow;
    }
    if (greeting !== (stored.greeting ?? '')) out['greeting'] = greeting === '' ? null : greeting;
    if (voiceGreeting !== (stored.voice_greeting ?? '')) {
      out['voice_greeting'] = voiceGreeting === '' ? null : voiceGreeting;
    }
    const avoidNow = avoid
      .split('\n')
      .map((w) => w.trim())
      .filter((w) => w !== '');
    const tone: Record<string, unknown> = {};
    if (toneVoice !== (stored.tone?.voice ?? '')) {
      tone['voice'] = toneVoice === '' ? null : toneVoice;
    }
    if (formality !== '' && formality !== stored.tone?.formality) tone['formality'] = formality;
    if (avoidNow.join('\n') !== (stored.tone?.avoid ?? []).join('\n')) tone['avoid'] = avoidNow;
    if (Object.keys(tone).length > 0) out['tone'] = tone;
    if (voiceMode !== '' && voiceMode !== stored.voice_mode) out['voice_mode'] = voiceMode;
    if (ownerNumber !== (stored.owner_number ?? '')) {
      out['owner_number'] = ownerNumber === '' ? null : ownerNumber;
    }

    if (Object.keys(out).length === 0) {
      onNothing();
      return;
    }
    onSave(out as AgentSettingsPatch);
  };

  return (
    <div className="editor">
      <h3>{t('concierge.autonomy')}</h3>
      {AUTONOMY_LEVELS.map((level) => (
        <label key={level} className="checkbox">
          <input
            type="radio"
            name="concierge-autonomy"
            checked={autonomy === level}
            onChange={() => setAutonomy(level)}
          />
          <span>{autonomyBlurb(t, level)}</span>
        </label>
      ))}

      <label className="field">
        <span>{t('concierge.ceiling')}</span>
        <input
          inputMode="numeric"
          value={ceiling}
          aria-label={t('concierge.ceiling')}
          onChange={(e) => setCeiling(e.target.value)}
        />
      </label>

      <label className="field">
        <span>{t('concierge.greeting')}</span>
        <input
          value={greeting}
          aria-label={t('concierge.greeting')}
          onChange={(e) => setGreeting(e.target.value)}
        />
      </label>

      <label className="field">
        <span>{t('concierge.voiceGreeting')}</span>
        <input
          value={voiceGreeting}
          aria-label={t('concierge.voiceGreeting')}
          onChange={(e) => setVoiceGreeting(e.target.value)}
        />
        <small>{t('concierge.voiceGreetingHint')}</small>
      </label>

      <label className="field">
        <span>{t('concierge.toneVoice')}</span>
        <input
          value={toneVoice}
          aria-label={t('concierge.toneVoice')}
          onChange={(e) => setToneVoice(e.target.value)}
        />
      </label>

      <label className="field">
        <span>{t('concierge.formality')}</span>
        <select
          value={formality}
          aria-label={t('concierge.formality')}
          onChange={(e) => setFormality(e.target.value)}
        >
          <option value="" />
          {FORMALITIES.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      </label>

      <label className="field">
        <span>{t('concierge.voiceMode')}</span>
        <select
          value={voiceMode}
          aria-label={t('concierge.voiceMode')}
          onChange={(e) => setVoiceMode(e.target.value)}
        >
          <option value="" />
          {VOICE_MODES.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <small>{voiceModeBlurb(t, voiceMode)}</small>
      </label>

      <label className="field">
        <span>{t('concierge.ownerNumber')}</span>
        <input
          value={ownerNumber}
          aria-label={t('concierge.ownerNumber')}
          placeholder="+13405550100"
          onChange={(e) => setOwnerNumber(e.target.value)}
        />
        <small>{t('concierge.ownerNumberHint')}</small>
      </label>

      <label className="field">
        <span>{t('concierge.avoid')}</span>
        <textarea
          value={avoid}
          rows={3}
          aria-label={t('concierge.avoid')}
          onChange={(e) => setAvoid(e.target.value)}
        />
        <small>{t('concierge.avoidHint')}</small>
      </label>

      <button type="button" className="button button--primary" disabled={busy} onClick={submit}>
        {t('concierge.save')}
      </button>
    </div>
  );
}

function autonomyBlurb(t: T, level: string): string {
  switch (level) {
    case 'conservative':
      return t('concierge.autonomy.conservative');
    case 'balanced':
      return t('concierge.autonomy.balanced');
    case 'autonomous':
      return t('concierge.autonomy.autonomous');
    default:
      return level;
  }
}

function voiceModeBlurb(t: T, mode: string): string {
  switch (mode) {
    case 'disabled':
      return t('concierge.voiceMode.disabled');
    case 'ai_first':
      return t('concierge.voiceMode.ai_first');
    case 'owner_first':
      return t('concierge.voiceMode.owner_first');
    default:
      return '';
  }
}

/**
 * The join the whole synced catalog exists for: **their list, our names.**
 *
 * The ids are ours, so the match is exact rather than by name — which is the
 * incident behind the entire handoff (a hand-copied catalog whose ids nothing
 * validated). An id the agent holds that this app's mirror does not know is
 * shown with "not synced yet — Sync now" rather than hidden, because hiding it
 * would hide a service the AI is currently allowed to sell.
 */
function ServiceSwitches({
  partner,
  services,
  busy,
  onToggle,
}: {
  partner: ConciergePartner;
  services: Service[];
  busy: boolean;
  onToggle: (id: string, next: boolean) => void;
}): ReactNode {
  const t = useT();
  const rows = partner.settings?.services ?? [];
  const byId = new Map(services.map((s) => [s.service_id, s]));

  return (
    <div className="editor">
      <h3>{t('concierge.services')}</h3>
      <p className="muted">{t('concierge.servicesNote')}</p>
      {rows.length === 0 ? (
        <p className="muted">{t('concierge.servicesEmpty')}</p>
      ) : (
        <ul className="service-list">
          {rows.map((row) => {
            const service = byId.get(row.id) ?? null;
            return (
              <li key={row.id}>
                <div>
                  <strong>{service?.name ?? row.id}</strong>
                  {service === null ? <small>{t('concierge.notSynced')}</small> : null}
                </div>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={row.bookable_by_ai}
                    disabled={busy}
                    aria-label={`${service?.name ?? row.id} — ${t('concierge.mayBook')}`}
                    onChange={(e) => onToggle(row.id, e.target.checked)}
                  />
                  <span>{t('concierge.mayBook')}</span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
